/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { css, jsx } from '@compiled/react';
import { FormattedMessage } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { fg } from '@atlaskit/platform-feature-flags';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { GitbookIcon } from '../../../../../../../common/constants/3p-product-configs/gitbook/icon';
import { ProductKeys } from '../../../../../../../common/constants/products';
import type { ResultClickedCallbackParams } from '../../../../../../../common/constants/types';
import type { SearchGraphResultProps } from '../../../../../../../common/types';
import { getDocumentSubtypeLabel } from '../../../../../../../common/ui/3p-result-labeling';
import { DescriptionBody } from '../../../../../../../common/ui/description-body';
import {
	EmbeddedComments,
	type EmbeddedComment,
} from '../../../../../../../common/ui/embedded-comments';
import { LastModified } from '../../../../../../../common/ui/last-modified';
import {
	MentionedByLinkedResults,
	SimilarLinkedResults,
} from '../../../../../../../common/ui/linked-results';
import { RestrictedLozenge } from '../../../../../../../common/ui/restricted-lozenge';
import { SearchResult } from '../../../../../../../common/ui/search-result';
import { SearchResultThumbnail } from '../../../../../../../common/ui/search-result-thumbnail';
import {
	enrichExcerpt,
	isNonPublic,
	processExcerpt,
} from '../../../../../../../common/utils/search-result-utils';

import type { searchGraphDocument_AtlassianSearchPage$key } from './__generated__/searchGraphDocument_AtlassianSearchPage.graphql';

const footerContainer = xcss({
	paddingTop: 'space.100',
});

const labelContainer = xcss({
	'::first-letter': {
		textTransform: 'capitalize',
	},
});

const contentIconStyles = css({
	width: token('space.400'),
	height: token('space.400'),
	paddingTop: token('space.025'),
	paddingRight: token('space.025'),
	paddingBottom: token('space.025'),
	paddingLeft: token('space.025'),
	boxSizing: 'border-box',
	objectFit: 'contain',
});

export const SearchGraphDocument: ({
	node,
	position,
	cloudId,
	selectedProduct,
	primaryProduct,
	appMode,
	product,
	icon,
	onResultClicked,
	onResultContextMenu,
	onCopyLinkButtonClicked,
	onSummarizeInChatButtonClicked,
	onContextMenuCopyButtonClicked,
	onContextMenuButtonClicked,
	subtype,
	thirdPartyConfigs,
	mainContentWidth,
	is3pGroupingEnabled,
}: SearchGraphResultProps<searchGraphDocument_AtlassianSearchPage$key>) => JSX.Element = ({
	node,
	position,
	cloudId,
	selectedProduct,
	primaryProduct,
	appMode,
	product,
	icon,
	onResultClicked,
	onResultContextMenu,
	onCopyLinkButtonClicked,
	onSummarizeInChatButtonClicked,
	onContextMenuCopyButtonClicked,
	onContextMenuButtonClicked,
	subtype,
	thirdPartyConfigs,
	mainContentWidth,
	is3pGroupingEnabled,
}: SearchGraphResultProps<searchGraphDocument_AtlassianSearchPage$key>) => {
	const data = useFragment(
		graphql`
			fragment searchGraphDocument_AtlassianSearchPage on SearchResultGraphDocument
			@argumentDefinitions(includeThirdPartyId: { type: "Boolean!", defaultValue: true }) {
				__typename
				id
				title
				url
				excerpt
				lastModifiedDate
				permissionLevel
				entity {
					... on ExternalDocument {
						thirdPartyId @include(if: $includeThirdPartyId)
						type {
							iconUrl
						}
						nonIndexableContent @optIn(to: "ExternalDataDepotQueryV2") {
							mediaThumbnailUrl
						}
						parent @optIn(to: "ExternalDataDepotQueryV2") {
							... on ExternalDocument {
								displayName
							}
						}
						container @optIn(to: "ExternalDataDepotQueryV2") {
							... on ExternalSpace {
								displayName
								url
							}
						}
					}
				}
				linkedEntities {
					id
					parentId
					excerpt
					url
					entity {
						... on ExternalComment {
							displayName
						}
					}
				}
				linkedResults {
					id
					title
					url
					type
					description
					lastModifiedDate
					category
					subtype
					integrationId
					providerId
					connectorType
				}
			}
		`,
		node,
	);

	/**
	 * A wrapper to include thirdPartyId in the onResultClicked callback.
	 */
	const onResultClickedWithThirdPartyId = useCallback(
		(params: ResultClickedCallbackParams = {}) => {
			const thirdPartyId = data.entity?.thirdPartyId || undefined;
			return onResultClicked?.({ ...params, thirdPartyId });
		},
		[onResultClicked, data],
	);

	/**
	 * A wrapper to include thirdPartyId in the onResultContextMenu callback.
	 */
	const onResultContextMenuWithThirdPartyId = useCallback(
		(params: ResultClickedCallbackParams = {}) => {
			const thirdPartyId = data.entity?.thirdPartyId || undefined;
			return onResultContextMenu?.({ ...params, thirdPartyId });
		},
		[onResultContextMenu, data],
	);

	const { id, url, title, excerpt, entity, lastModifiedDate, permissionLevel, linkedEntities } =
		data;
	const isNotPublic = isNonPublic(permissionLevel);
	const parentName = entity?.parent?.displayName || null;
	const containerName = entity?.container?.displayName || null;

	// Transform linkedEntities (comments) into EmbeddedComment format
	const embeddedComments: EmbeddedComment[] = useMemo(() => {
		if (!linkedEntities?.length) {
			return [];
		}
		return linkedEntities
			.filter((linkedEntity) => linkedEntity.excerpt)
			.map(
				(linkedEntity): EmbeddedComment => ({
					id: linkedEntity.id,
					parentId: linkedEntity.parentId ?? null,
					excerpt: linkedEntity.excerpt ?? null,
					url: linkedEntity.url,
					authorName: linkedEntity.entity?.displayName ?? null,
				}),
			);
	}, [linkedEntities]);

	// Memoized URL validation function to avoid recreation on every render
	const isValidIconUrl = useCallback((url: string): boolean => {
		try {
			const parsedUrl = new URL(url);
			// Only allow https URLs for security
			return parsedUrl.protocol === 'https:';
		} catch {
			return false;
		}
	}, []);

	// Get icon URL and validate it
	const iconUrl = entity?.type?.iconUrl;
	const validatedIconUrl = useMemo(() => {
		return iconUrl && isValidIconUrl(iconUrl) ? iconUrl : null;
	}, [iconUrl, isValidIconUrl]);

	// State to track if the icon image failed to load
	const [iconLoadError, setIconLoadError] = useState(false);

	// Handle icon load error
	const handleIconError = useCallback(() => {
		setIconLoadError(true);
	}, []);

	// Reset error state when iconUrl changes
	useEffect(() => {
		setIconLoadError(false);
	}, [validatedIconUrl]);

	// Memoize document icon creation to avoid recreation when props haven't changed
	const documentIcon = useMemo(() => {
		// GitBook support - use GitbookIcon when selectedProduct is 'gitbook'
		if (selectedProduct === 'gitbook') {
			return <GitbookIcon />;
		}

		if (!validatedIconUrl || iconLoadError) {
			return icon;
		}
		return (
			<img
				src={validatedIconUrl}
				css={contentIconStyles}
				onError={handleIconError}
				alt=""
				role="presentation"
			/>
		);
	}, [selectedProduct, validatedIconUrl, iconLoadError, handleIconError, icon]);

	const shouldShowLastModified = lastModifiedDate && product !== ProductKeys.AtlassianWebCrawler;
	const lastModifiedComponent = shouldShowLastModified ? (
		<LastModified lastModified={lastModifiedDate} />
	) : null;

	// We overwrite some subtype labels by product, eg for presentation->PowerPoint
	const label = getDocumentSubtypeLabel({ product, subtype });
	const subtypeComponent = subtype && (
		<Box xcss={labelContainer}>{subtype?.replace(/-/g, ' ')}</Box>
	);
	const labelComponent = label && (
		<Box xcss={labelContainer}>
			<FormattedMessage {...label} />
		</Box>
	);

	const headerMetadataParts: [string, ReactNode][] = [
		['label', labelComponent || subtypeComponent || null],
		['containerName', containerName],
		['parentName', parentName || null],
		['lastModified', lastModifiedComponent],
	];

	const enrichedTitle = enrichExcerpt(title);

	const mediaThumbnailUrl = entity?.nonIndexableContent?.mediaThumbnailUrl;
	const [thumbnailLoadError, setThumbnailLoadError] = useState(false);
	const shouldAttemptThumbnail =
		subtype === 'image' && mediaThumbnailUrl && fg('rovo_fps_3p_document_image_thumbnail');

	const description =
		shouldAttemptThumbnail && !thumbnailLoadError ? (
			<SearchResultThumbnail
				resultUrl={url}
				thumbnailUrl={mediaThumbnailUrl}
				onError={() => setThumbnailLoadError(true)}
			/>
		) : (
			excerpt && <DescriptionBody description={processExcerpt(excerpt)} />
		);
	const linkedResults = data.linkedResults?.map((linkedResult) => ({
		id: linkedResult.id,
		title: linkedResult.title,
		url: linkedResult.url,
		type: linkedResult.type,
		description: linkedResult.description,
		lastModifiedDate: linkedResult.lastModifiedDate,
		category: linkedResult.category,
		subtype: linkedResult.subtype,
		integrationId: linkedResult.integrationId,
		providerId: linkedResult.providerId,
		connectorType: linkedResult.connectorType,
	}));

	const shouldShowRelatedLinks = linkedResults && linkedResults.length > 0;
	const isEmbeddedCommentsEnabled = fg('enable_3p_embedded_comments_search');
	const hasEmbeddedComments = isEmbeddedCommentsEnabled && embeddedComments.length > 0;

	const shouldRenderSimilarResults = linkedResults?.some(
		(linkedResult) => linkedResult.category?.toUpperCase() === 'SIMILAR',
	);

	const isMentionedByEnabledForDriveSharepoint = fg('rovo_fps_enable_gdrive_sharepoint_mentioned');
	const shouldShowMentionedByLinkedResults =
		!selectedProduct ||
		(isMentionedByEnabledForDriveSharepoint &&
			(selectedProduct === ProductKeys.GoogleDrive || selectedProduct === ProductKeys.Sharepoint));

	// When a document has both linkedResults and linkedEntities (comments), only linkedResults are shown.
	const footerComponent = shouldShowRelatedLinks ? (
		shouldRenderSimilarResults ? (
			<SimilarLinkedResults
				linkedResults={linkedResults || []}
				thirdPartyConfigs={thirdPartyConfigs}
				partial={data}
				url={url}
				position={position}
				onResultClicked={onResultClickedWithThirdPartyId}
				isAllProductSelected={!selectedProduct}
				onResultContextMenu={onResultContextMenuWithThirdPartyId}
				is3pGroupingEnabled={is3pGroupingEnabled}
				product={product}
			/>
		) : (
			<MentionedByLinkedResults
				linkedResults={linkedResults || []}
				thirdPartyConfigs={thirdPartyConfigs}
				partial={data}
				url={url}
				onResultClicked={onResultClickedWithThirdPartyId}
				position={position}
				onResultContextMenu={onResultContextMenuWithThirdPartyId}
				isAllProductSelected={shouldShowMentionedByLinkedResults}
			/>
		)
	) : hasEmbeddedComments ? (
		<EmbeddedComments comments={embeddedComments} />
	) : (
		isNotPublic && (
			<Box xcss={footerContainer}>
				<RestrictedLozenge restriction={permissionLevel} />
			</Box>
		)
	);

	return (
		<SearchResult
			{...data}
			id={id}
			product={product}
			productSelected={!!selectedProduct}
			primaryProduct={primaryProduct}
			onResultClicked={onResultClickedWithThirdPartyId}
			onResultContextMenu={onResultContextMenuWithThirdPartyId}
			onCopyLinkButtonClicked={onCopyLinkButtonClicked}
			onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
			onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
			onContextMenuButtonClicked={onContextMenuButtonClicked}
			url={url}
			title={enrichedTitle}
			icon={documentIcon}
			headerMetadataParts={headerMetadataParts}
			description={description}
			cloudId={cloudId}
			position={position}
			partial={data}
			appMode={appMode}
			isThirdParty
			footer={footerComponent}
			hasRelatedLinks={!!linkedResults && linkedResults.length > 0}
			mainContentWidth={mainContentWidth}
		/>
	);
};
